// =============================================================================
// ui/shell/LogButton.tsx — the activity log, and the badge that says look at it.
// -----------------------------------------------------------------------------
// ── 🚨 WITHOUT THIS, A FAILURE IS SILENCE ──
//
// Every surface in this client reports its own trouble inline — "Couldn't load
// your dashboard", "Import failed" — and every one of those messages is gone the
// moment you navigate away. Anything that fails while you are looking somewhere
// else is never reported at all: a rejected promise, a background read, a write
// that came back 403.
//
// So the honest state before this file was: **the app could fail and the user
// would have no way to find out.** That is the whole argument for porting it,
// and it is why this was the one worth taking first of the five controls #51
// still lists. Josh's words: *"it's how a user finds out something failed."*
//
// ── ⚠ WHAT IT SHOWS, AND WHY THAT IS NOT WHAT BETA SHOWS ──
//
// The old client logs EVERY toast — info and success included — because a toast
// is how it says everything, so its log doubles as a history of what you did.
//
// 🔴 THIS CLIENT HAS NO TOAST SYSTEM AT ALL. Not an omission: every ported
// surface deliberately renders its own inline status instead, and several
// comments say so. There is therefore no stream of info/success events to log,
// and inventing one to fill the panel would be building a notification system
// under cover of porting a button.
//
// ▶ So this shows errors and warnings — including uncaught ones and rejected
// promises, which are the ones no surface reports. The panel SAYS so rather than
// calling itself an activity log and quietly holding half of one. If toasts ever
// land here, they call `record()` and appear with no change to this file.
//
// ── ⚠ THE STORE IS AN io/ MODULE, NOT CONTEXT ──
//
// The capture has to be installed BEFORE React mounts — an error thrown while
// the app is booting is exactly the one worth keeping — so the buffer cannot
// live in a provider. `useSyncExternalStore` is the supported way to read a
// mutable external store, and the store exposes a version NUMBER for it. Handing
// that hook a fresh array would spin React in an infinite re-render.
// =============================================================================

import { useCallback, useMemo, useState, useSyncExternalStore } from 'react';
import {
  clearErrorLog, logEntries, logVersion, markLogRead, reportFailure,
  subscribeLog, unreadLogCount, type LoggedError,
} from '../../io/diagnostics/errorLog';

/** Relative age, matching the old client's thresholds. */
function ago(ts: number, now: number): string {
  const sec = Math.floor((now - ts) / 1000);
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return new Date(ts).toLocaleString();
}

export function LogButton() {
  // ⚠ The version, not the entries — see the header.
  const version = useSyncExternalStore(subscribeLog, logVersion, logVersion);
  const [open, setOpen] = useState(false);

  // ⚠ Recomputed only when the store changes. `version` is the whole dependency;
  // it is referenced here rather than in the body so the rule can see it.
  const entries = useMemo(() => { void version; return logEntries(); }, [version]);
  const unread = useMemo(() => { void version; return unreadLogCount(); }, [version]);

  const openPanel = useCallback(() => {
    setOpen(true);
    // ⚠ Marked read on OPEN, as in the old client — not on close. Closing
    // without reading is a decision the user made; the badge should not nag
    // afterwards for entries they chose to skip.
    markLogRead();
  }, []);

  return (
    <>
      <button
        type="button"
        className="btn"
        onClick={openPanel}
        title="Activity log — errors and warnings from this session"
        aria-label={unread ? `Activity log, ${unread} unread` : 'Activity log'}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
            stroke="currentColor" strokeWidth="2" strokeLinejoin="round"
          />
          <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
          <line x1="16" y1="13" x2="8" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <line x1="16" y1="17" x2="8" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
        Log
        {/* ⚠ Rendered only when there is something unread — never as a zero.
            A badge showing 0 is a permanent small alarm that means nothing. */}
        {unread > 0 ? (
          <span className="log-badge">{unread > 99 ? '99+' : unread}</span>
        ) : null}
      </button>

      {open ? <LogPanel entries={entries} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function LogPanel(
  { entries, onClose }: { entries: readonly LoggedError[]; onClose: () => void },
) {
  // ⚠ Read ONCE per open, not per row. Rows would otherwise each format against
  // a slightly different "now", and two entries a millisecond apart could render
  // different ages.
  const now = useMemo(() => Date.now(), []);
  // Newest first — the reason anyone opens this is the thing that just happened.
  const rows = useMemo(() => [...entries].reverse(), [entries]);

  const errors = rows.filter((e) => e.kind === 'error').length;
  const warns = rows.filter((e) => e.kind === 'warn').length;

  const [copied, setCopied] = useState<string | null>(null);

  const copy = useCallback(async () => {
    const text = rows.map((e) => (
      `[${new Date(e.ts).toISOString()}] [${e.kind.toUpperCase()}] ${e.msg}`
      + (e.source ? `\n  source: ${e.source}` : '')
      + (e.stack ? `\n${e.stack}` : '')
    )).join('\n\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied('Copied to the clipboard.');
    } catch (e) {
      // ⚠ Named, not swallowed. The clipboard is refused outright on an insecure
      // origin and in some embedded views, and "nothing happened" is the worst
      // possible answer for the button whose entire job is handing you the text.
      //
      // ⚠ This is the one reporter whose entry lands in the list it is standing
      // in front of: the panel re-renders and the copy failure appears at the
      // top. That is the right place for it — and it is a single user gesture,
      // not a render, so it cannot feed itself.
      setCopied(`Couldn’t copy: ${reportFailure('copy activity log', e)}`);
    }
  }, [rows]);

  return (
    <div className="modal-scrim" role="dialog" aria-modal="true" aria-label="Activity log">
      <div className="modal wide">
        <header className="modal-head">
          <b>
            Activity log — {rows.length} {rows.length === 1 ? 'entry' : 'entries'}
            {errors ? ` · ${errors} error${errors === 1 ? '' : 's'}` : ''}
            {warns ? ` · ${warns} warning${warns === 1 ? '' : 's'}` : ''}
          </b>
        </header>

        <div className="modal-body">
          {rows.length === 0 ? (
            /* ⚠ An EMPTY log is good news and says so. "No activity logged yet"
               reads like a feature that has not started working. */
            <p className="muted">
              Nothing has gone wrong this session. Errors and warnings show up here
              as they happen.
            </p>
          ) : (
            <div className="log-list">
              {rows.map((e, i) => (
                <div className={`log-entry ${e.kind}`} key={`${e.ts}-${i}`}>
                  <div className="log-entry-head">
                    <span className="mono muted log-time">
                      {new Date(e.ts).toLocaleTimeString()}
                    </span>
                    <span className="grow log-msg">{e.msg}</span>
                    <span className={`tag log-kind ${e.kind}`}>{e.kind}</span>
                    <span className="muted log-ago">{ago(e.ts, now)}</span>
                  </div>
                  {e.source ? <div className="mono muted log-source">{e.source}</div> : null}
                  {/* ⚠ The stack is the field that makes a report actionable, so
                      it is shown rather than hidden behind a disclosure. */}
                  {e.stack ? <pre className="log-stack">{e.stack}</pre> : null}
                </div>
              ))}
            </div>
          )}

          <p className="muted log-note">
            Errors and warnings only, held in memory for this session — a reload
            clears them. This client shows successes inline on the screen that
            caused them rather than collecting them here.
          </p>
          {copied ? <p className="muted" role="status">{copied}</p> : null}
        </div>

        <footer className="modal-foot">
          <button
            type="button" className="btn danger"
            disabled={rows.length === 0}
            onClick={() => { clearErrorLog(); onClose(); }}
          >
            Clear log
          </button>
          <span className="grow" />
          <button
            type="button" className="btn"
            disabled={rows.length === 0}
            onClick={() => { void copy(); }}
          >
            Copy as text
          </button>
          <button type="button" className="btn primary" onClick={onClose}>Close</button>
        </footer>
      </div>
    </div>
  );
}
